import React, { useEffect, useState } from "react";
import { Product } from "../../types/product.type";
import {
  Typography,
  Input,
  Select,
  Space,
  Checkbox,
  Table,
  DatePicker,
  Spin,
  Button,
  Form,
  message,
  Modal,
  InputNumber,
  Menu,
  Dropdown,
} from "antd";
import { UnitOfMeasurement } from "../../types/unitOfMeasurement.type";
import useGetRequest from "../../hooks/useGetRequest";
import { Produce } from "../../types/produce.type";
import { DownOutlined } from "@ant-design/icons";
import { api } from "../../api/api";
import { useNavigate } from "react-router-dom";
import { Material } from "../../types/material.type";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [unitFilter, setUnitFilter] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [inStock, setInStock] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[any, any] | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const { data: materialsList, loading: materialLoading } =
    useGetRequest(`/material/list`);
  const { data: producesList, loading: listLoading } =
    useGetRequest(`/produce/list`);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchProducts();
  }, [
    page,
    searchTerm,
    unitFilter,
    sortBy,
    sortOrder,
    pageSize,
    inStock,
    dateRange,
  ]);

  const fetchProducts = async () => {
    setLoading(true);

    try {
      const response = await api.get(`/product`, {
        params: {
          page,
          limit: pageSize,
          name: searchTerm,
          unitOfMeasurement: unitFilter,
          sortBy,
          sortOrder,
          inStock: inStock ? true : undefined,
          startDate: dateRange ? dateRange[0]?.format("YYYY-MM-DD") : undefined,
          endDate: dateRange ? dateRange[1]?.format("YYYY-MM-DD") : undefined,
        },
      });

      setProducts(response.data.products);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching products", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };
  const handleAddProduct = () => {
    form.resetFields();
    setSelectedProduct(null);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    form.setFieldsValue(product);
    setSelectedProduct(product);
    setIsModalOpen(true);
  };
  const handleFormSubmit = async (values: Partial<Product>) => {
    setConfirmLoading(true);
    const materials = values.materials || [];
    const produces = values.produces || [];

    const materialIds = materials.map((material) => material.materialId);
    const hasDuplicates = materialIds.some(
      (id, index) => materialIds.indexOf(id) !== index
    );

    const produceIds = produces.map((produce) => produce.produceId);
    const hasProduceDuplicates = produceIds.some(
      (id, index) => produceIds.indexOf(id) !== index
    );
    if (hasDuplicates || hasProduceDuplicates) {
      message.error(
        "Нельзя использовать одну и ту же продукцию несколько раз."
      );
      return;
    }
    if (produces.length === 0) {
      message.error("Добавьте хотя бы одну продукцию.");
      return;
    }
    try {
      if (selectedProduct) {
        await api.put(`/product/${selectedProduct._id}`, values);
        message.success("Продукт обновлен.");
      } else {
        await api.post(`/product`, values);
        message.success("Продут добавлен.");
      }
      fetchProducts();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving material:", error);
      message.error(error.response.data.error);
    }
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Ед. изм",
      dataIndex: "unitOfMeasurement",
      key: "unitOfMeasurement",
    },
    {
      title: "Материалы",
      key: "materials",
      render: (_: any, record: Product) => (
        <ul>
          {record.materials.map((material) => (
            <li key={material.materialId}>
              {material.materialName} ({material.quantity})
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: "Продукции",
      key: "produces",
      render: (_: any, record: Produce) => (
        <ul>
          {record.produces.map((produce) => (
            <li key={produce.produceId}>
              {produce.produceName} ({produce.quantity})
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
    },
    {
      title: "Произведено всего",
      dataIndex: "totalProduced",
      key: "totalProduced",
    },
    {
      title: "Произведено днем",
      dataIndex: "totalProducedDay",
      key: "totalProducedDay",
    },
    {
      title: "Произведено ночью",
      dataIndex: "totalProducedNight",
      key: "totalProducedNight",
    },
    {
      title: "Продано (кол-во)",
      dataIndex: "totalSold",
      key: "totalSold",
    },
    {
      title: "Куплено (кол-во)",
      dataIndex: "totalPurchased",
      key: "totalPurchased",
    },
    {
      title: "Продано в USD",
      key: "totalPaidAmount",
      render: (_: any, product: Product) => product.totalSoldAmount.USD,
    },
    {
      title: "Продано в UZS",
      key: "totalPaidAmount",
      render: (_: any, product: Product) => product.totalSoldAmount.UZS,
    },
    {
      title: "Куплено в USD)",
      key: "totalPaidAmount",
      render: (_: any, product: Product) => product.totalPurchasedAmount.USD,
    },
    {
      title: "Куплено в UZS)",
      key: "totalPaidAmount",
      render: (_: any, product: Product) => product.totalPurchasedAmount.UZS,
    },
    {
      title: "Получено в USD",
      key: "totalReceivedAmount",
      render: (_: any, product: Product) => product.totalReceivedAmount.USD,
    },
    {
      title: "Получено в UZS",
      key: "totalReceivedAmount",
      render: (_: any, product: Product) => product.totalReceivedAmount.UZS,
    },
    {
      title: "Заплачено в USD",
      key: "totalPaidAmount",
      render: (_: any, product: Product) => product.totalPaidAmount.USD,
    },
    {
      title: "Заплачено в UZS",
      key: "totalPaidAmount",
      render: (_: any, product: Product) => product.totalPaidAmount.UZS,
    },
    {
      title: "Отходы",
      dataIndex: "wasteName",
      key: "wasteName",
      render: (value: string | undefined) =>
        value ? value : <i>Нет данных</i>,
    },
    {
      title: "Количество отходов",
      dataIndex: "wasteQuantity",
      key: "wasteQuantity",
      render: (value: number | undefined) =>
        value !== undefined ? value : <i>Нет данных</i>,
    },
    {
      title: "Ед. изм. отхода",
      dataIndex: "wasteUnit",
      key: "wasteUnit",
      render: (value: number | undefined) =>
        value !== undefined ? value : <i>Нет данных</i>,
    },
    {
      title: "Остаток отхода",
      dataIndex: "residueWaste",
      key: "residueWaste",
      render: (value: number | undefined) =>
        value !== undefined ? value : <i>Нет данных</i>,
    },
    {
      title: "",
      key: "actions",
      render: (_: any, product: Product) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => handleEditProduct(product)}>
              Изменить
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(`/${product._id}/product/${product.name}/batches`)
              }
            >
              Смотреть партии
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(`/${product._id}/product/${product.name}/reports`)
              }
            >
              Смотреть отчеты
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(`/${product._id}/product/${product.name}/workers`)
              }
            >
              Смотреть сотрудников
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(`/${product._id}/kind/${product.name}/orders`)
              }
            >
              Смотреть заказы
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(`/${product._id}/product/${product.name}/changelogs`)
              }
            >
              Смотреть журнал
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Товары</Title>
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Фильтр по ед. измерения"
          onChange={(value) => setUnitFilter(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(UnitOfMeasurement).map((unit) => (
            <Option key={unit} value={unit}>
              {unit}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Сортировать по"
          defaultValue="name"
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="name">Название</Option>
          <Option value="totalProduced">Произведено</Option>
          <Option value="createdAt">Дата</Option>
          <Option value="residue">Остаток</Option>
          <Option value="totalSold">Продано</Option>
          <Option value="totalPurchased">Куплено</Option>
        </Select>
        <Select
          defaultValue="asc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Checkbox
          checked={inStock}
          onChange={(e) => setInStock(e.target.checked)}
        >
          В наличии
        </Checkbox>

        <RangePicker
          onChange={(dates) => setDateRange(dates ?? undefined)}
          placeholder={["Начало", "Конец"]}
        />
        <Button type="primary" onClick={handleAddProduct}>
          Добавить
        </Button>
      </Space>

      {loading ? (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 50 }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={[]}
          dataSource={products}
          pagination={{
            current: page,
            total: total,
            pageSize: pageSize,
            onChange: (page, size) => {
              setPage(page);
              if (size !== pageSize) setPageSize(size!);
            },
          }}
          rowKey="_id"
          scroll={{ x: "max-content" }}
          style={{ marginTop: 16 }}
        />
      )}
      <Modal
        title={selectedProduct ? "Изменить товар" : "Добавить товар"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleFormSubmit} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="unitOfMeasurement"
            label="Ед. измерения"
            rules={[{ required: true, message: "Выберите единицу измерения" }]}
          >
            <Select placeholder="Выберите единицу измерения">
              {Object.values(UnitOfMeasurement).map((unit) => (
                <Option key={unit} value={unit}>
                  {unit}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.List name="produces">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, fieldKey, ...restField }) => (
                  <Space key={key} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, "produceId"]}
                      fieldKey={[fieldKey as number, "produceId"]}
                      rules={[
                        { required: true, message: "Выберите продукцию" },
                      ]}
                    >
                      <Select
                        placeholder="Выберите продукцию"
                        loading={listLoading}
                        style={{ width: 200 }}
                      >
                        {producesList?.map((produce: Produce) => (
                          <Option key={produce._id} value={produce._id}>
                            {produce.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "quantity"]}
                      fieldKey={[fieldKey as number, "quantity"]}
                      rules={[
                        { required: true, message: "Введите количество" },
                      ]}
                    >
                      <InputNumber min={0} placeholder="Количество" />
                    </Form.Item>
                    <Button type="link" onClick={() => remove(name)}>
                      Удалить
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  Добавить продукцию
                </Button>
              </>
            )}
          </Form.List>

          <div style={{ marginTop: 20 }}>
            <Form.List name="materials">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "materialId"]}
                        fieldKey={[fieldKey as number, "materialId"]}
                        rules={[
                          { required: true, message: "Выберите материал" },
                        ]}
                      >
                        <Select
                          placeholder="Материал"
                          loading={materialLoading}
                        >
                          {materialsList?.map((material: Material) => (
                            <Option key={material._id} value={material._id}>
                              {material.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "quantity"]}
                        fieldKey={[fieldKey as number, "quantity"]}
                        rules={[
                          {
                            required: true,
                            message: "Введите кол-во",
                          },
                        ]}
                      >
                        <InputNumber
                          min={0}
                          placeholder="Кол-во использования"
                        />
                      </Form.Item>
                      <Button type="link" onClick={() => remove(name)}>
                        Удалить
                      </Button>
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add()} block>
                    Добавить материал
                  </Button>
                </>
              )}
            </Form.List>
          </div>
          <Form.Item name="wasteName" label="Название отхода">
            <Input />
          </Form.Item>

          <Form.Item name="wasteUnit" label="Ед. измерения отхода">
            <Select placeholder="Выберите единицу измерения отхода">
              {Object.values(UnitOfMeasurement).map((unit) => (
                <Option key={unit} value={unit}>
                  {unit}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
